import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, IndianRupee, MessageSquare } from 'lucide-react';
import { motion } from 'framer-motion';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import { COURSE_IMAGES } from '../../config/courseImages';
import { heroPhotoScrim } from '../../content/heroThemes';
import { useAuth } from '../../context/AuthContext';
import { useMyContributorApplications } from '../../hooks/useMyContributorApplications';
import { INTERVIEW_PAGE, INTERVIEW_TECHNOLOGIES } from '../../content/interviewsPage';
import { interviewApi, InterviewConfig } from '../../services/api';
import { contentApi } from '../../services/contentApi';
import { formatCurrency } from '../../utils/currency';
import { getUserProfileId } from '../../utils/userProfile';
import {
  courseInterviewSlotBadge,
  extractInterviewList,
  findCourseSessionRequest,
  isCourseInterviewSlotUsed,
  notifyCourseInterviewsChanged,
} from '../../utils/courseInterviewSessions';
import { InterviewRequestItem } from '../../types';

const interviewSchema = z
  .object({
    technology: z.string().min(1, 'Please select a technology'),
    otherTechnology: z.string().optional(),
    yearsOfExperience: z
      .number({ invalid_type_error: 'Years of experience is required' })
      .min(0, 'Enter a valid number')
      .max(50, 'Enter a valid number'),
    experienceDetails: z.string().min(10, 'Please describe your relevant experience'),
    role: z.string().min(2, 'Current or desired role is required'),
    roleDetails: z.string().min(10, 'Please add role details'),
    linkedInUrl: z
      .string()
      .url('Please enter a valid URL')
      .refine((value) => value.toLowerCase().includes('linkedin.com'), {
        message: 'Please enter a LinkedIn URL',
      }),
    additionalNotes: z.string().optional(),
  })
  .refine(
    (data) =>
      data.technology !== 'Other' ||
      (data.otherTechnology && data.otherTechnology.trim().length > 1),
    {
      message: 'Please specify the technology',
      path: ['otherTechnology'],
    }
  );

type InterviewFormData = z.infer<typeof interviewSchema>;

const fieldClasses = `block w-full rounded-md border-2 border-gray-300 shadow-sm bg-white text-gray-900
  placeholder:text-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20
  px-4 py-2.5 text-base transition duration-200 ease-in-out`;
const labelClasses = 'block text-sm font-semibold text-gray-900 mb-1.5';
const errorClasses = 'mt-1.5 text-sm text-error-600 font-medium';

const InterviewsHome: React.FC = () => {
  const { user, isAuthenticated, isProfileComplete, isLoading: authLoading } = useAuth();
  const { canApplyInterviewer } = useMyContributorApplications();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const contentCourseId = Number(searchParams.get('contentCourseId') || 0) || null;
  const courseTitle = searchParams.get('courseTitle') || '';
  const courseSession = Number(searchParams.get('session') || 0) || null;
  const isCoursePackage = !!(contentCourseId && contentCourseId > 0);
  const [config, setConfig] = useState<InterviewConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(!isCoursePackage);
  const [feeReady, setFeeReady] = useState(isCoursePackage);
  const [seatsFull, setSeatsFull] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [packageGate, setPackageGate] = useState<string | null>(null);
  const [existingSession, setExistingSession] = useState<InterviewRequestItem | null>(null);
  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (isCoursePackage) setShowRequestForm(true);
  }, [isCoursePackage]);

  useEffect(() => {
    if (!isCoursePackage || !contentCourseId) {
      setPackageGate(null);
      setExistingSession(null);
      return;
    }
    if (!isAuthenticated) return;
    const profileId = getUserProfileId(user);
    if (!profileId) return;

    let cancelled = false;
    Promise.all([
      contentApi.getCourseEnrollment(contentCourseId).catch(() => null),
      interviewApi.getByUser(profileId).catch(() => null),
    ]).then(([enrollmentResponse, interviewsResponse]) => {
      if (cancelled) return;
      const purchased = enrollmentResponse?.data.enrollment?.interviewPackageCount
        ?? (enrollmentResponse?.data.enrollment as { InterviewPackageCount?: number } | null | undefined)?.InterviewPackageCount
        ?? 0;
      const session = courseSession || 1;
      if (!enrollmentResponse?.data.enrollment?.isPaid) {
        setPackageGate('Enroll in this course before scheduling a included review session.');
        setExistingSession(null);
        return;
      }
      if (purchased <= 0) {
        setPackageGate('This enrollment does not include review sessions.');
        setExistingSession(null);
        return;
      }
      if (session < 1 || session > purchased) {
        setPackageGate(`You purchased ${purchased} review session${purchased === 1 ? '' : 's'}. Session ${session} is not available.`);
        setExistingSession(null);
        return;
      }
      const match = findCourseSessionRequest(
        extractInterviewList(interviewsResponse?.data),
        contentCourseId,
        session,
        courseTitle || undefined
      );
      if (match && isCourseInterviewSlotUsed(match.status)) {
        setExistingSession(match);
        setPackageGate(`Interview ${session} is already ${courseInterviewSlotBadge(match.status).toLowerCase()}. You cannot schedule it again.`);
        return;
      }
      setExistingSession(null);
      setPackageGate(null);
    });
    return () => { cancelled = true; };
  }, [isCoursePackage, contentCourseId, courseSession, isAuthenticated, user]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<InterviewFormData>({
    resolver: zodResolver(interviewSchema),
    defaultValues: {
      technology: '',
      otherTechnology: '',
      experienceDetails: '',
      role: '',
      roleDetails: '',
      linkedInUrl: '',
      additionalNotes: '',
    },
  });

  const selectedTechnology = watch('technology');

  useEffect(() => {
    if (showRequestForm) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      formHeadingRef.current?.focus();
    }
  }, [showRequestForm]);

  useEffect(() => {
    if (submitted) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      successHeadingRef.current?.focus();
    }
  }, [submitted]);

  useEffect(() => {
    const loadConfig = async () => {
      if (!isCoursePackage) setConfigLoading(true);
      try {
        const [response, catalog] = await Promise.all([
          interviewApi.getConfig(),
          contentApi.getCatalogSettings().catch(() => null),
        ]);
        const catalogSettings = catalog?.data?.settings;
        if (response.data.success) {
          const defaultPrice = catalogSettings?.interviewDefaultPrice ?? response.data.defaultPrice;
          const discountPercent = catalogSettings?.interviewDiscountPercent ?? response.data.discountPercent ?? 0;
          const payablePrice = catalogSettings?.interviewPayablePrice
            ?? response.data.payablePrice
            ?? defaultPrice;
          setConfig({
            defaultDurationMinutes: response.data.defaultDurationMinutes,
            defaultPrice,
            discountPercent,
            payablePrice,
            technologies: response.data.technologies?.length
              ? response.data.technologies
              : [...INTERVIEW_TECHNOLOGIES],
            maxActiveSlots: response.data.maxActiveSlots,
            activeSlotCount: response.data.activeSlotCount,
            seatsFull: response.data.seatsFull,
          });
          setSeatsFull(!!response.data.seatsFull && !isCoursePackage);
          if (!isCoursePackage) setFeeReady(true);
        }
      } catch {
        setConfig({
          defaultDurationMinutes: INTERVIEW_PAGE.durationMinutes,
          defaultPrice: 0,
          discountPercent: 0,
          payablePrice: 0,
          technologies: [...INTERVIEW_TECHNOLOGIES],
        });
      } finally {
        setConfigLoading(false);
      }
    };
    loadConfig();
  }, [isCoursePackage]);

  const onSubmit = async (data: InterviewFormData) => {
    if (!isAuthenticated) {
      navigate('/login', {
        state: {
          message: 'Please sign in to request an interview.',
          from: location.pathname,
        },
      });
      return;
    }

    const profileId = getUserProfileId(user);
    if (!profileId) {
      navigate('/profile-setup');
      return;
    }

    if (!isProfileComplete) {
      navigate('/profile-setup');
      return;
    }

    if (!user?.isEmailVerified) {
      navigate('/verify-email');
      return;
    }

    if (isCoursePackage && packageGate) {
      setError(packageGate);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      const response = await interviewApi.request({
        userProfileId: profileId,
        technology: data.technology,
        otherTechnology: data.technology === 'Other' ? data.otherTechnology : undefined,
        yearsOfExperience: data.yearsOfExperience,
        experienceDetails: data.experienceDetails,
        role: data.role,
        roleDetails: data.roleDetails,
        linkedInUrl: data.linkedInUrl,
        additionalNotes: data.additionalNotes || undefined,
        contentCourseId: isCoursePackage ? contentCourseId! : undefined,
        contentCourseTitle: isCoursePackage ? (courseTitle || undefined) : undefined,
        courseInterviewSession: isCoursePackage ? (courseSession || 1) : undefined,
      });
      if (response.data.success) {
        const status = response.data.interviewRequest?.status;
        setWaitlisted(status === 'Interested' || /interest|waitlist/i.test(response.data.message || ''));
        setSubmitted(true);
        notifyCourseInterviewsChanged();
      } else {
        setError(response.data.message || 'Interview request could not be submitted.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Interview request could not be submitted.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const technologies = config?.technologies?.length ? config.technologies : [...INTERVIEW_TECHNOLOGIES];

  const renderGate = () => {
    if (authLoading) {
      return null;
    }

    if (!isAuthenticated) {
      return (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p>Sign in to submit an interview request.</p>
          <Link
            to="/login"
            state={{ from: location.pathname, message: 'Please sign in to request an interview.' }}
            className="mt-3 inline-flex"
          >
            <Button variant="primary" size="sm">
              Sign in to continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      );
    }

    if (!isProfileComplete) {
      return (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p>Complete your profile before you can request an interview.</p>
          <Link to="/profile-setup" className="mt-3 inline-flex">
            <Button variant="primary" size="sm">
              Complete profile
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      );
    }

    if (!user?.isEmailVerified) {
      return (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p>Verify your email before you can request an interview.</p>
          <Link to="/verify-email" className="mt-3 inline-flex">
            <Button variant="primary" size="sm">
              Verify email
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      );
    }

    return null;
  };

  const canSubmit = isAuthenticated && isProfileComplete && !!user?.isEmailVerified && !packageGate;
  const durationLabel = INTERVIEW_PAGE.durationDisplay;
  const priceReady = isCoursePackage || feeReady;
  const listPrice = isCoursePackage ? 0 : (config?.defaultPrice ?? 0);
  const payablePrice = isCoursePackage ? 0 : (config?.payablePrice ?? 0);
  const discountPercent = config?.discountPercent ?? 0;
  const listPriceLabel = formatCurrency(listPrice);
  const displayPriceLabel = formatCurrency(payablePrice);
  const limitedOfferNote = !priceReady
    ? ''
    : isCoursePackage
    ? 'Included with your course review package'
    : (listPrice > 0 && discountPercent > 0
      ? `${discountPercent}% off for a limited period`
      : (listPrice <= 0 ? 'Trial — no interview fee' : 'Current interview fee'));
  const formNote = isCoursePackage
    ? `This review session is included with ${courseTitle || 'your course'} (session ${courseSession || 1}). Fee: ₹0.`
    : !priceReady
      ? `Tell us your stack and role. Sessions are ${INTERVIEW_PAGE.durationMinutes} minutes.`
      : listPrice > 0 && discountPercent > 0
      ? `Tell us your stack and role. Sessions are ${INTERVIEW_PAGE.durationMinutes} minutes. Technical Interview is ${listPriceLabel} — currently ${displayPriceLabel} (${discountPercent}% off for a limited period).`
      : `Tell us your stack and role. Sessions are ${INTERVIEW_PAGE.durationMinutes} minutes. Technical Interview fee: ${displayPriceLabel}.`;

  const feeProgress = (tone: 'light' | 'dark' = 'dark') => (
    <progress
      className={`mt-1 h-2 w-28 overflow-hidden rounded-full ${
        tone === 'light' ? 'accent-white' : 'accent-primary-600'
      }`}
      aria-label="Loading Technical Interview fee"
    />
  );

  const rangeCards = (
    <div className="mt-5 grid w-[90%] grid-cols-2 gap-2.5">
      <Card className="p-3">
        <p className="text-[10px] uppercase tracking-wide text-gray-500">{INTERVIEW_PAGE.durationLabel}</p>
        <p className="mt-0.5 text-base font-semibold text-gray-900">{durationLabel}</p>
      </Card>
      <Card className="p-3">
        <p className="text-[10px] uppercase tracking-wide text-gray-500">{INTERVIEW_PAGE.priceLabel}</p>
        {!priceReady ? (
          <div className="mt-2">
            {configLoading ? feeProgress() : <p className="text-sm text-gray-500">Fee will be confirmed</p>}
            {configLoading ? <p className="mt-1 text-[10px] text-gray-500">Loading fee…</p> : null}
          </div>
        ) : (
          <>
            <p className="mt-0.5 text-base font-semibold text-gray-900">
              {!isCoursePackage && listPrice > 0 && payablePrice < listPrice && (
                <span className="mr-2 text-sm font-medium text-gray-400 line-through">{formatCurrency(config?.defaultPrice ?? 0)}</span>
              )}
              {displayPriceLabel}
            </p>
            {limitedOfferNote ? <p className="text-[10px] text-gray-500">{limitedOfferNote}</p> : null}
          </>
        )}
        {isCoursePackage && courseTitle ? (
          <p className="mt-1 text-xs font-semibold text-primary-800">{courseTitle}{courseSession ? ` · Session ${courseSession}` : ''}</p>
        ) : null}
      </Card>
    </div>
  );

  return (
    <div className="lg:grid lg:grid-cols-2 lg:items-start">
      {(isSubmitting || (showRequestForm && authLoading)) && <LoadingOverlay />}

      <aside className="relative h-[16rem] sm:h-[20rem] lg:sticky lg:top-[4.5rem] lg:h-[calc(100dvh-4.5rem)] lg:self-start overflow-hidden bg-sky-950">
        <img
          src={COURSE_IMAGES.interviewHero}
          alt="Candidate in a remote technical interview"
          className="absolute inset-0 h-full w-full object-cover object-[center_20%]"
        />
        <div className={heroPhotoScrim('interviews')} />
        <div className="relative z-10 flex h-full flex-col justify-end p-6 sm:p-8 lg:p-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-100 ring-1 ring-white/20">
              <MessageSquare className="h-3.5 w-3.5" />
              {INTERVIEW_PAGE.eyebrow}
            </span>
            <h1 className="mt-4 text-xl sm:text-2xl lg:text-[1.65rem] font-bold leading-tight">
              {INTERVIEW_PAGE.heroTitle}
            </h1>
            <p className="mt-3 max-w-md text-sm text-primary-50/95 leading-relaxed">
              {INTERVIEW_PAGE.heroIntro}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium ring-1 ring-white/20">
                <Clock className="h-3.5 w-3.5" />
                {durationLabel}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium ring-1 ring-white/20">
                <IndianRupee className="h-3.5 w-3.5" />
                {!priceReady ? (
                  configLoading ? feeProgress('light') : <span className="text-sm">Fee TBC</span>
                ) : (
                  <>
                    {!isCoursePackage && listPrice > 0 && payablePrice < listPrice && (
                      <span className="line-through opacity-70">{formatCurrency(config?.defaultPrice ?? 0)}</span>
                    )}
                    <span>{displayPriceLabel}</span>
                  </>
                )}
              </span>
            </div>
          </motion.div>
        </div>
      </aside>

      <section className="relative flex flex-col px-4 py-6 sm:px-8 lg:px-10 lg:min-h-[calc(100dvh-4.5rem)]">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-xl mx-auto w-full"
        >
          {!showRequestForm && !submitted && (
            <>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">{INTERVIEW_PAGE.title}</h2>
              <p className="mt-3 text-sm md:text-base text-gray-600 leading-relaxed">{INTERVIEW_PAGE.intro}</p>

              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-primary-600">
                {INTERVIEW_PAGE.includesEyebrow}
              </p>
              <ul className="mt-3 space-y-2">
                {INTERVIEW_PAGE.includes.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" />
                    <span className="text-sm font-medium text-gray-900">{item}</span>
                  </li>
                ))}
              </ul>

              {rangeCards}

              {seatsFull && (
                <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  Interview seats are currently full. You can express interest and we will email you when a seat opens.
                </div>
              )}

              <div className="mt-5">
                <Button
                  type="button"
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    setShowRequestForm(true);
                    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                  }}
                >
                  {seatsFull ? 'Express interest' : INTERVIEW_PAGE.ctaRequest}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                {canApplyInterviewer && (
                  <p className="mt-3 text-sm text-gray-500">
                    Industry expert?{' '}
                    <Link to="/interviewer-registration" className="font-medium text-primary-700 hover:text-primary-800">
                      Apply as an interviewer
                    </Link>
                  </p>
                )}
              </div>
            </>
          )}

          {(showRequestForm || submitted) && (
            <div>
              {!submitted && (
                <button
                  type="button"
                  onClick={() => setShowRequestForm(false)}
                  className="mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-primary-700"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to overview
                </button>
              )}
              {rangeCards}
              <Card className="mt-4 p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-6">
                <CheckCircle className="h-10 w-10 text-emerald-600 mx-auto" />
                <h2
                  ref={successHeadingRef}
                  tabIndex={-1}
                  className="mt-4 text-xl font-semibold text-gray-900 outline-none"
                >
                  {waitlisted ? 'Interest registered' : 'Request submitted'}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                  {waitlisted
                    ? 'You are on the interview interest list. We will email you when a seat becomes available. Track status in your Dashboard.'
                    : 'Your interview is pending administrator confirmation. Track status and the meeting link in your Dashboard.'}
                </p>
                <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link to="/dashboard?tab=interviews">
                    <Button variant="primary">Go to dashboard</Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline">Back to home</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <h3
                  ref={formHeadingRef}
                  tabIndex={-1}
                  className="text-lg font-semibold text-gray-900 outline-none"
                >
                  {seatsFull ? 'Express interest' : 'Book a session'}
                </h3>
                <p className="mt-1 mb-5 text-sm text-gray-600">
                  {seatsFull
                    ? 'Seats are full. Submit your details to join the interest list — we will email you when a seat opens.'
                    : formNote}
                </p>
                {renderGate()}
                {packageGate && (
                  <div className="mb-4 rounded-md border border-indigo-200 bg-indigo-50 p-3 text-sm text-indigo-900">
                    <p>{packageGate}</p>
                    {existingSession?.status?.toLowerCase() === 'completed' && (
                      <p className="mt-1 font-semibold text-emerald-700">This review session is completed.</p>
                    )}
                    <Link to="/dashboard?tab=interviews" className="mt-2 inline-block font-semibold text-primary-800 hover:text-primary-900">
                      View in dashboard
                    </Link>
                  </div>
                )}
                {error && (
                  <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                    {error}
                  </div>
                )}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                  <div>
                    <label className={labelClasses} htmlFor="technology">
                      Technology
                    </label>
                    <select
                      id="technology"
                      className={fieldClasses}
                      disabled={!canSubmit}
                      {...register('technology')}
                    >
                      <option value="">Select a technology</option>
                      {technologies.map((tech) => (
                        <option key={tech} value={tech}>
                          {tech}
                        </option>
                      ))}
                    </select>
                    {errors.technology && <p className={errorClasses}>{errors.technology.message}</p>}
                  </div>

                  {selectedTechnology === 'Other' && (
                    <Input
                      label="Specify technology"
                      disabled={!canSubmit}
                      {...register('otherTechnology')}
                      error={errors.otherTechnology?.message}
                    />
                  )}

                  <Input
                    label="Years of experience"
                    type="number"
                    step="0.5"
                    min={0}
                    max={50}
                    disabled={!canSubmit}
                    {...register('yearsOfExperience', { valueAsNumber: true })}
                    error={errors.yearsOfExperience?.message}
                  />

                  <div>
                    <label className={labelClasses} htmlFor="experienceDetails">
                      Relevant experience details
                    </label>
                    <textarea
                      id="experienceDetails"
                      rows={4}
                      disabled={!canSubmit}
                      className={fieldClasses}
                      placeholder="Projects, stacks, and the kind of work you have done"
                      {...register('experienceDetails')}
                    />
                    {errors.experienceDetails && (
                      <p className={errorClasses}>{errors.experienceDetails.message}</p>
                    )}
                  </div>

                  <Input
                    label="Current / desired role"
                    disabled={!canSubmit}
                    placeholder="e.g. Backend Engineer, Full Stack Developer"
                    {...register('role')}
                    error={errors.role?.message}
                  />

                  <div>
                    <label className={labelClasses} htmlFor="roleDetails">
                      Role details
                    </label>
                    <textarea
                      id="roleDetails"
                      rows={3}
                      disabled={!canSubmit}
                      className={fieldClasses}
                      placeholder="What you do today, or the role you are targeting"
                      {...register('roleDetails')}
                    />
                    {errors.roleDetails && <p className={errorClasses}>{errors.roleDetails.message}</p>}
                  </div>

                  <Input
                    label="LinkedIn URL"
                    type="url"
                    disabled={!canSubmit}
                    placeholder="https://www.linkedin.com/in/your-profile"
                    {...register('linkedInUrl')}
                    error={errors.linkedInUrl?.message}
                  />

                  <div>
                    <label className={labelClasses} htmlFor="additionalNotes">
                      Additional notes
                    </label>
                    <textarea
                      id="additionalNotes"
                      rows={4}
                      disabled={!canSubmit}
                      className={fieldClasses}
                      placeholder="Custom requirements, areas you want evaluated, or special instructions"
                      {...register('additionalNotes')}
                    />
                    {errors.additionalNotes && (
                      <p className={errorClasses}>{errors.additionalNotes.message}</p>
                    )}
                  </div>

                  <Button type="submit" variant="primary" fullWidth disabled={!canSubmit || isSubmitting}>
                    {isSubmitting
                      ? 'Submitting…'
                      : seatsFull
                        ? 'Express interest'
                        : 'Submit interview request'}
                  </Button>
                </form>
              </>
            )}
          </Card>
            </div>
          )}
        </motion.div>
      </section>
    </div>
  );
};

export default InterviewsHome;
